import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import { chevronDownIcon } from "@progress/kendo-svg-icons";
import { ComboBoxLabel, ComboPrefixIcon } from "components/Dashboard";
import { useCurrentUser } from "services/currentUserService";
import { useCompany } from "services/companyService";
import {
  ACCESS_LEVELS,
  ALL_BRANCH,
  ALL_FEE_EARNER,
  ALL_TEAM,
  buildBranchOptions,
  buildFeeEarnerOptions,
  buildTeamOptions,
  getAccessLevel,
  normalizeFeeEarnerSelection,
} from "./accessProfileFilters";
import { DateRange } from "./DateRange";
import { formatDateForAPI } from "./formatter";

const EMPTY_BRANCHES = Object.freeze([]);

const getDefaultDateRange = (yearStartDate) => {
  const requestedStart = new Date(yearStartDate);
  const startDate = Number.isNaN(requestedStart.getTime())
    ? new Date()
    : requestedStart;
  const dayOfWeek = startDate.getDay();
  const startOfWeek = new Date(startDate);

  startOfWeek.setDate(
    startDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1),
  );

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setFullYear(startOfWeek.getFullYear() + 1);
  endOfWeek.setDate(endOfWeek.getDate() - 1);

  return { startOfWeek, endOfWeek };
};

export const FilterComponent = (props) => {
  const { data: user, fetched: currentUserFetched } = useCurrentUser();
  const { data: company } = useCompany();
  const updateFiltersRef = useRef(props.updateFilters);
  updateFiltersRef.current = props.updateFilters;

  const accessLevel = getAccessLevel(props.allowAccessData);
  const branches = props.allowAccessData?.branches || EMPTY_BRANCHES;
  const { startOfWeek, endOfWeek } = useMemo(
    () => getDefaultDateRange(company?.yearStartDate),
    [company?.yearStartDate],
  );

  const [comboBoxState, setComboBoxState] = useState({
    branchId: ALL_BRANCH,
    teamId: ALL_TEAM,
    feeEarnerRef: ALL_FEE_EARNER,
  });
  const [initialized, setInitialized] = useState(false);
  const [duration, setDuration] = useState("");
  const [toDate, setToDate] = useState(endOfWeek);
  const [fromDate, setFromDate] = useState(startOfWeek);

  const branchOptions = useMemo(
    () => buildBranchOptions(branches),
    [branches],
  );
  const teamOptions = useMemo(
    () =>
      buildTeamOptions(
        branches,
        comboBoxState.branchId?.branchId,
        accessLevel,
      ),
    [accessLevel, branches, comboBoxState.branchId?.branchId],
  );
  const feeEarnerOptions = useMemo(
    () =>
      buildFeeEarnerOptions(
        branches,
        comboBoxState.teamId?.teamID,
        accessLevel,
      ),
    [accessLevel, branches, comboBoxState.teamId?.teamID],
  );

  useEffect(() => {
    if (!currentUserFetched || !props.allowAccessData) return;

    const showsFeeEarner =
      !props.disabledFeeEarner && accessLevel !== ACCESS_LEVELS.NONE;
    const initialFeeEarnerOptions = buildFeeEarnerOptions(
      branches,
      ALL_TEAM.teamID,
      accessLevel,
    );

    setComboBoxState({
      branchId: ALL_BRANCH,
      teamId: ALL_TEAM,
      feeEarnerRef: showsFeeEarner
        ? normalizeFeeEarnerSelection(
            initialFeeEarnerOptions,
            null,
            user,
          )
        : ALL_FEE_EARNER,
    });
    setInitialized(true);
  }, [
    accessLevel,
    branches,
    currentUserFetched,
    props.allowAccessData,
    props.disabledFeeEarner,
    user,
  ]);

  useEffect(() => {
    if (!initialized) return;

    updateFiltersRef.current?.({
      feeEarnerRef: comboBoxState.feeEarnerRef?.userRef || "ALL",
      teamId: comboBoxState.teamId?.teamID ?? ALL_TEAM.teamID,
      branch: comboBoxState.branchId?.branchId ?? ALL_BRANCH.branchId,
      ToDate: formatDateForAPI(toDate),
      FromDate: formatDateForAPI(fromDate),
      duration,
    });
  }, [comboBoxState, duration, fromDate, initialized, toDate]);

  const DASHBOARD_FILTERS = props.component === "Dashboard";
  const inlineLayout = props.inlineLayout === true;
  const feeEarnerLabel = props.feeEarnerLabel || "Fee Earner";

  const filterColClass = DASHBOARD_FILTERS
    ? "col-sm-12 col-md-6 col-lg-4 col-xl-3"
    : "col";

  const toolbarFilterStyle = !DASHBOARD_FILTERS
    ? props.filterWidth
      ? { width: props.filterWidth, flexShrink: 0 }
      : { minWidth: "175px", maxWidth: "225px", flexShrink: 0 }
    : undefined;

  const handleDateChange = (newFromDate, newToDate, newDuration) => {
    setFromDate(newFromDate);
    setToDate(newToDate);
    setDuration(newDuration);
  };

  const handleBranchChange = (event) => {
    const branchId = event.value || ALL_BRANCH;
    const nextFeeEarnerOptions = buildFeeEarnerOptions(
      branches,
      ALL_TEAM.teamID,
      accessLevel,
    );

    setComboBoxState((current) => ({
      branchId,
      teamId: ALL_TEAM,
      feeEarnerRef: normalizeFeeEarnerSelection(
        nextFeeEarnerOptions,
        current.feeEarnerRef,
        user,
      ),
    }));
  };

  const handleTeamChange = (event) => {
    const teamId = event.value || ALL_TEAM;
    const nextFeeEarnerOptions = buildFeeEarnerOptions(
      branches,
      teamId.teamID,
      accessLevel,
    );

    setComboBoxState((current) => ({
      ...current,
      teamId,
      feeEarnerRef: normalizeFeeEarnerSelection(
        nextFeeEarnerOptions,
        current.feeEarnerRef,
        user,
      ),
    }));
  };

  const handleFeeEarnerChange = (event) => {
    setComboBoxState((current) => ({
      ...current,
      feeEarnerRef: event.value || ALL_FEE_EARNER,
    }));
  };

  const renderLabel = (name, dashboardName = name) =>
    DASHBOARD_FILTERS ? (
      <ComboBoxLabel name={dashboardName} />
    ) : (
      <Label className="filter-font mb-1">{name}</Label>
    );

  const renderPrefix = (name) =>
    DASHBOARD_FILTERS ? <ComboPrefixIcon name={name} /> : undefined;

  const filterContent = (
    <>
      {accessLevel === ACCESS_LEVELS.ALL && (
        <div
          className={`d-flex flex-column ${filterColClass}`}
          style={toolbarFilterStyle}
        >
          {renderLabel("Branch")}
          <ComboBox
            style={{ width: "100%", height: "40px" }}
            data={branchOptions}
            textField="branchName"
            className={DASHBOARD_FILTERS ? undefined : "ps-2"}
            prefix={renderPrefix("Branch")}
            svgIcon={chevronDownIcon}
            filterable
            dataItemKey="branchId"
            placeholder="Search Branch"
            value={comboBoxState.branchId}
            onChange={handleBranchChange}
          />
        </div>
      )}

      {(accessLevel === ACCESS_LEVELS.BRANCH ||
        accessLevel === ACCESS_LEVELS.ALL) && (
        <div
          className={`d-flex flex-column ${filterColClass}`}
          style={toolbarFilterStyle}
        >
          {renderLabel("Team")}
          <ComboBox
            prefix={renderPrefix("Team")}
            style={{ height: "40px", width: "100%" }}
            data={teamOptions}
            dataItemKey="teamID"
            svgIcon={chevronDownIcon}
            textField="teamName"
            value={comboBoxState.teamId}
            onChange={handleTeamChange}
            placeholder="Search Team"
          />
        </div>
      )}

      {accessLevel !== ACCESS_LEVELS.NONE && !props.disabledFeeEarner && (
        <div
          className={`d-flex flex-column ${filterColClass}`}
          style={toolbarFilterStyle}
        >
          {renderLabel(feeEarnerLabel, "Feeearner")}
          <ComboBox
            style={{ width: "100%", height: "40px" }}
            data={feeEarnerOptions}
            textField="display"
            filterable
            className={DASHBOARD_FILTERS ? undefined : "ps-2"}
            prefix={renderPrefix("Feeearner")}
            dataItemKey="userRef"
            svgIcon={chevronDownIcon}
            placeholder="Search Fee Earner"
            value={comboBoxState.feeEarnerRef}
            onChange={handleFeeEarnerChange}
          />
        </div>
      )}

      {DASHBOARD_FILTERS && (
        <div className={`d-flex flex-column ${filterColClass}`}>
          <DateRange
            fromDate={fromDate}
            toDate={toDate}
            onDateChange={handleDateChange}
            yearStartDate={company?.yearStartDate}
            yearEndDate={company?.yearEndDate}
            icon
            fillWidth
            style={{ width: "100%", maxWidth: "326px", height: "40px" }}
            dropdownItems={[
              "Today",
              "This Week",
              "Past Week",
              "This Month",
              "Quarter",
              "Financial Year",
              "Calendar Year",
            ]}
          />
        </div>
      )}
    </>
  );

  if (inlineLayout) return filterContent;

  return (
    <div className="row g-2 align-items-end flex-wrap">{filterContent}</div>
  );
};
